import { useCallback } from "react"
import { useSelector } from "react-redux"

/**
 * A day in the feed is expected to carry `blackouts`, but the endpoint is not built yet and the
 * sibling blackout endpoints have already shipped shapes the contract did not describe. Reading
 * through this keeps a malformed day from white-screening every calendar view.
 */
const toBlackouts = (day) => (Array.isArray(day?.blackouts) ? day.blackouts : [])

/**
 * Reads the day-resolved blackout feed (`getCalendarBlackouts`) and answers the two
 * questions every calendar surface asks: "is this day blacked out?" and "is this booking
 * in conflict?".
 *
 * The booking side is derived on the client because calendar bookings carry `experience_name`
 * but no experience id, so a booking can only be matched to a blackout by listing *title*. That
 * is ambiguous: two listings sharing a title make one of them report a conflict it does not have.
 * Fixing it needs the backend — either `blackout_conflict` per booking (preferred, and already
 * read first by the reservation detail page) or an experience id on each calendar booking.
 */
export const useCalendarBlackouts = () => {
  const days = useSelector((state) => state.blackouts?.calendar?.days || {})
  const isLoading = useSelector((state) => Boolean(state.blackouts?.calendar?.loading))

  const getDay = useCallback((dateKey) => days[dateKey] || null, [days])

  const isBlackedOut = useCallback((dateKey) => Boolean(days[dateKey]), [days])

  /**
   * The blackout a booking conflicts with, or null.
   *
   * The server decides this now: every booking carries `blackout_conflict`, resolved against the
   * booking's own crew and equipment assignments (including the boat→asset cascade). The client
   * used to infer it by comparing the experience *title*, which was wrong whenever two listings
   * shared a name — that stopgap is gone.
   *
   * `booking_ids` on the day's conflicts is the same answer from the other direction, so a
   * booking whose payload predates the field is still caught. Those ids are the numeric booking
   * id — the time-grid events keep it as `booking_id`, because their `id` is the uuid.
   */
  const getBookingConflict = useCallback(
    (booking) => {
      if (booking?.blackout_conflict) return booking.blackout_conflict

      const dateKey = booking?.date
      const day = dateKey && days[dateKey]
      const bookingId = booking?.booking_id ?? booking?.id
      if (!day || bookingId == null) return null

      return toBlackouts(day).find((blackout) => (blackout.booking_ids || []).some((id) => String(id) === String(bookingId))) || null
    },
    [days]
  )

  return { days, isLoading, getDay, isBlackedOut, getBookingConflict }
}

export default useCalendarBlackouts
